import { legalConfig as cfg } from '../legalConfig';
import { LegalDoc } from '../types';

export const termsDoc: LegalDoc = {
  slug: 'terms',
  title: 'Terms of Use',
  effectiveDate: cfg.effectiveDate,
  lastUpdated: cfg.lastUpdated,
  sections: [
    {
      heading: '1. Who we are',
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} is operated by ${cfg.entityName}, a ${cfg.entityForm} registered in ${cfg.launchMarket} under registration number ${cfg.registrationNumber}. Our registered or principal address is ${cfg.physicalAddress}. You can contact us at ${cfg.supportEmail} or ${cfg.telephone}.`,
        },
        {
          type: 'p',
          text: `In these Terms, "${cfg.brandName}", "we", "us", and "our" refer to ${cfg.entityName}. "Platform" refers to the ${cfg.brandName} website, mobile application, and related services.`,
        },
      ],
    },
    {
      heading: '2. Acceptance of these Terms',
      blocks: [
        {
          type: 'p',
          text: 'By creating an account, accessing, or using the Platform, you agree to these Terms and the Community Guidelines. Our Privacy Policy explains how we process personal information and is not part of these Terms unless the law requires otherwise.',
        },
        { type: 'p', text: 'If you do not agree, do not use the Platform.' },
      ],
    },
    {
      heading: '3. Eligibility',
      blocks: [
        { type: 'p', text: 'You may use the Platform only if:' },
        {
          type: 'ul',
          items: [
            'you are at least 18 years old;',
            'you can enter into a binding agreement under South African law;',
            'the information you provide is accurate and not misleading; and',
            'we have not previously suspended or removed you for serious or repeated misconduct.',
          ],
        },
        {
          type: 'p',
          text: `${cfg.brandName} is not intended for children. Do not create an account for anyone under 18.`,
        },
      ],
    },
    {
      heading: `4. What ${cfg.brandName} provides`,
      blocks: [
        { type: 'p', text: 'The Platform provides tools that may allow users to:' },
        {
          type: 'ul',
          items: [
            'create a roommate or property profile;',
            'discover and express interest in other users or listings;',
            'receive compatibility suggestions based on selected preferences;',
            'match and communicate with other users;',
            'invite a landlord or property representative into a chat; and',
            'report, block, or remove users where those features are available.',
          ],
        },
        {
          type: 'p',
          text: `${cfg.brandName} provides a technology and communication platform. We do not guarantee that a profile is genuine, that information is accurate, that a user is suitable, that a property exists or is safe, or that a match will lead to a successful living arrangement.`,
        },
      ],
    },
    {
      heading: `5. ${cfg.brandName} is not a party to housing arrangements`,
      blocks: [
        {
          type: 'p',
          text: `Unless ${cfg.brandName} expressly agrees otherwise in writing, ${cfg.brandName} is not:`,
        },
        {
          type: 'ul',
          items: [
            'a landlord, tenant, subtenant, estate agent, property practitioner, credit provider, guarantor, or insurer;',
            'a party to negotiations, a lease, deposit arrangement, payment, inspection, or dispute between users; or',
            'responsible for property condition, rent, deposits, utilities, conduct in a shared home, or performance of a lease.',
          ],
        },
        {
          type: 'p',
          text: `Users must conduct their own checks, view a property safely, confirm ownership or authority to let, understand the lease, and obtain professional advice where needed. Never send money merely because someone appears trustworthy on ${cfg.brandName}.`,
        },
      ],
    },
    {
      heading: '6. Accounts and profile information',
      blocks: [
        {
          type: 'p',
          text: 'You are responsible for your account, login credentials, and activity. Keep your credentials confidential and notify us promptly if you suspect unauthorised access.',
        },
        { type: 'p', text: 'You must not:' },
        {
          type: 'ul',
          items: [
            'impersonate another person or organisation;',
            "use another person's photographs or details without permission;",
            'misrepresent your identity, age, authority, property, budget, or housing needs;',
            'create multiple deceptive accounts; or',
            'sell, transfer, or allow another person to use your account.',
          ],
        },
        {
          type: 'p',
          text: 'We may ask for information reasonably needed to protect the Platform or investigate a report. A verification indicator, if offered, means only that a particular check was completed at a point in time. It is not an endorsement, guarantee, criminal record check, financial assessment, or promise of safety.',
        },
      ],
    },
    {
      heading: '7. Matching and recommendations',
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} may use profile details, preferences, approximate location, budget, intended move date, and Platform activity to order or suggest profiles and listings. Compatibility is an estimate, not a professional assessment or guarantee.`,
        },
        {
          type: 'p',
          text: `You control whether to contact or meet another user. ${cfg.brandName} will not make a decision that creates a lease or other legal housing commitment for you.`,
        },
      ],
    },
    {
      heading: '8. Listings and landlord conduct',
      blocks: [
        {
          type: 'p',
          text: 'A person who creates or represents a property listing confirms that they have authority to do so and that the listing is accurate, lawful, current, and not misleading.',
        },
        {
          type: 'p',
          text: 'Landlords and property representatives must comply with applicable housing, consumer, equality, privacy, and advertising laws. They may not use the Platform to engage in unfair discrimination, unlawful screening, deceptive advertising, illegal deposits, harassment, or retaliation.',
        },
        {
          type: 'p',
          text: `${cfg.brandName} may request evidence of authority, correct or remove a listing, restrict messaging, or suspend an account where reasonably necessary to address risk or suspected misconduct.`,
        },
      ],
    },
    {
      heading: '9. Chats and landlord conversations',
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} keeps roommate conversations and landlord conversations separate. When a property inquiry is created, ${cfg.brandName} opens a dedicated conversation for that inquiry between the matched pair and the relevant landlord or property representative. A landlord does not receive access to your private roommate chat history.`,
        },
        {
          type: 'p',
          text: 'All participants in an inquiry conversation can see the messages and files sent within it. Do not share another person\'s identity documents, financial information, contact details, or other private information without their permission.',
        },
        {
          type: 'p',
          text: 'Participants may leave, block, report, or request removal where the relevant feature is available. Removal stops access to future content, but does not necessarily delete content already received. We may preserve relevant records where reasonably required for safety, fraud prevention, legal claims, or compliance.',
        },
      ],
    },
    {
      heading: '10. User content',
      blocks: [
        {
          type: 'p',
          text: 'You retain ownership of the text, photographs, messages, listings, and other content you submit.',
        },
        {
          type: 'p',
          text: `You give ${cfg.brandName} a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, adapt for technical display, and show your content only as reasonably necessary to operate, secure, improve, and provide the Platform. This licence ends when the content is deleted, subject to reasonable backup, safety, legal, and record retention requirements.`,
        },
        {
          type: 'p',
          text: "You confirm that you own your content or have permission to use it, and that it does not violate another person's rights or any law.",
        },
      ],
    },
    {
      heading: '11. Prohibited conduct',
      blocks: [
        { type: 'p', text: 'You must not use the Platform to:' },
        {
          type: 'ul',
          items: [
            'threaten, stalk, harass, intimidate, exploit, or harm another person;',
            'publish hate speech or engage in unfair discrimination;',
            'solicit sexual services or send unwanted sexual content;',
            'scam users, request deceptive payments, launder money, or commit fraud;',
            'advertise a property without authority;',
            'share identity documents, financial information, intimate content, or private communications without permission;',
            'scrape, copy, harvest, or sell user information;',
            'upload malware or interfere with Platform security or availability;',
            'create automated accounts or access the Platform through unauthorised bots;',
            'evade a suspension, block, or safety control; or',
            'encourage or assist unlawful conduct.',
          ],
        },
      ],
    },
    {
      heading: '12. Safety',
      blocks: [
        {
          type: 'p',
          text: 'Use care when communicating, viewing a property, or meeting someone. Meet in a public place first where appropriate, tell a trusted person where you are going, arrange your own transport, verify property and landlord details, review a written lease, and avoid cash or rushed deposits.',
        },
        {
          type: 'p',
          text: `If there is an immediate threat or emergency, contact the South African Police Service or the appropriate emergency service. Reporting something to ${cfg.brandName} is not a substitute for contacting emergency services or law enforcement.`,
        },
      ],
    },
    {
      heading: '13. Fees and payments',
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} is currently free to use. We do not currently process rent, deposits, or lease payments.`,
        },
        {
          type: 'p',
          text: 'Before introducing subscriptions, paid boosts, verification fees, or transaction payments, we will display the price, billing frequency, taxes, cancellation method, renewal terms, and applicable refund information before purchase. Paid features will be subject to additional terms where appropriate.',
        },
      ],
    },
    {
      heading: '14. Third-party services',
      blocks: [
        {
          type: 'p',
          text: `The Platform may link to maps, payment services, identity providers, social networks, or other third-party services. Their terms and privacy practices apply to their services. ${cfg.brandName} is not responsible for third-party services beyond what applicable law requires.`,
        },
      ],
    },
    {
      heading: `15. ${cfg.brandName} intellectual property`,
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} and its licensors own the Platform software, branding, design, databases, and other content provided by us. These Terms give you a limited, personal, revocable, non-transferable right to use the Platform for its intended purpose. They do not transfer ownership of ${cfg.brandName} intellectual property to you.`,
        },
      ],
    },
    {
      heading: '16. Moderation, suspension, and termination',
      blocks: [
        {
          type: 'p',
          text: 'We may investigate reports and may warn, restrict, suspend, or terminate an account, remove content, preserve evidence, or contact relevant authorities where reasonably necessary to:',
        },
        {
          type: 'ul',
          items: [
            'enforce these Terms or the Community Guidelines;',
            'protect users, us, or the public;',
            'prevent fraud, abuse, or security incidents; or',
            'comply with law or a valid legal request.',
          ],
        },
        {
          type: 'p',
          text: 'Where appropriate and safe, we may tell you the reason and provide a way to ask for review. We may act without prior notice where delay could increase risk, compromise an investigation, or prevent legal compliance.',
        },
        {
          type: 'p',
          text: `You may stop using ${cfg.brandName} and request account deletion from within the app or by emailing ${cfg.supportEmail}. Sections that by their nature should continue, including intellectual property, safety records, dispute provisions, and lawful limitations of liability, survive termination.`,
        },
      ],
    },
    {
      heading: '17. Important notice about risk and liability',
      blocks: [
        {
          type: 'note',
          text: 'Please read this section carefully. It limits certain risks and liabilities and should be displayed prominently before a user accepts the Terms.',
        },
        {
          type: 'p',
          text: `${cfg.brandName} is provided on an "as available" basis. To the extent permitted by law, we do not promise uninterrupted access, a particular match, successful roommate relationship, property availability, user behaviour, or the accuracy of user content.`,
        },
        {
          type: 'p',
          text: `${cfg.brandName} does not exclude or limit liability where doing so would be unlawful, including rights and remedies that cannot be waived under the Consumer Protection Act or other applicable law.`,
        },
        {
          type: 'p',
          text: `Subject to those non-excludable rights, ${cfg.brandName} is not responsible for indirect or consequential loss caused by interactions, housing arrangements, leases, payments, property condition, or conduct of users that ${cfg.brandName} did not cause and could not reasonably prevent.`,
        },
        {
          type: 'p',
          text: 'Nothing in these Terms requires a user to assume a risk or indemnify us beyond what South African law permits. Any limitation applies only to the extent that it is fair, reasonable, and lawful in the circumstances.',
        },
      ],
    },
    {
      heading: '18. Responsibility for claims caused by misuse',
      blocks: [
        {
          type: 'p',
          text: `To the extent permitted by law, you are responsible for loss or a third-party claim directly caused by your unlawful content, fraud, wilful misconduct, or material breach of these Terms. This provision does not apply to the extent that ${cfg.brandName} caused or contributed to the loss.`,
        },
      ],
    },
    {
      heading: '19. Complaints and disputes',
      blocks: [
        {
          type: 'p',
          text: `Contact us first at ${cfg.supportEmail} so that we can try to resolve a complaint promptly.`,
        },
        {
          type: 'p',
          text: `A dispute about a lease or rental practice may fall within the jurisdiction of a provincial Rental Housing Tribunal or a court. ${cfg.brandName} cannot decide a dispute between a landlord and tenant unless the dispute concerns ${cfg.brandName}'s own service.`,
        },
        {
          type: 'p',
          text: `These Terms are governed by the laws of ${cfg.launchMarket}. Subject to any consumer right to use another competent forum, disputes concerning ${cfg.brandName} may be brought before a court with jurisdiction in ${cfg.launchMarket}.`,
        },
      ],
    },
    {
      heading: '20. Changes to the Platform or these Terms',
      blocks: [
        {
          type: 'p',
          text: 'We may improve or change the Platform. If we make a material change to these Terms, we will give reasonable notice through the Platform, email, or another appropriate method before the change takes effect where required. The updated Terms will show a new effective date.',
        },
      ],
    },
    {
      heading: '21. Contact and legal notices',
      blocks: [
        {
          type: 'ul',
          items: [
            `General support: ${cfg.supportEmail}`,
            `Legal notices: ${cfg.legalEmail}`,
          ],
        },
      ],
    },
  ],
};
